import { addressService, Address as ApiAddress, CreateAddressRequest } from '../services/addressService';
import { authService } from '../services/authService';
import { masterDataService, State, City } from '../services/masterDataService';
import { safeJsonParse } from './securityUtils';

export interface Address {
  id: string;
  addressUuid?: string; // UUID field for backend compatibility
  type?: 'home' | 'work' | 'other'; // Optional for backward compatibility
  addressType?: 'home' | 'work' | 'other'; // New API field
  label: string; // Maps to 'name' in old interface
  name?: string; // For backward compatibility
  line1: string; // Maps to 'addressLine1'
  addressLine1?: string; // For backward compatibility
  line2?: string; // Maps to 'addressLine2'
  addressLine2?: string; // For backward compatibility
  
  // Master data references - using state/city field names for minimal API changes
  state: number;    // Master data state ID
  city: number;     // Master data city ID
  
  // Display names (resolved from entities or master data)
  stateName?: string;   // Display name for state
  cityName?: string;    // Display name for city
  stateCode?: string;   // State code for additional info
  
  postalCode: string; // Maps to 'pincode'
  pincode?: string; // For backward compatibility
  latitude?: string; // Changed to string to match API
  longitude?: string; // Changed to string to match API
  landmark?: string; // For backward compatibility
  isDefault: boolean; // For backward compatibility
  default?: boolean; // New API field
  isActive: boolean; // Field for soft delete functionality
  
  // Contact information for bookings at this address
  contactName?: string;   // Contact person name for this address
  contactPhone?: string;  // Contact person phone for this address
  
  createdAt: string;
  updatedAt: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  createdAt: string;
  updatedAt: string;
}

// Helper function to convert API address to local address format
const convertApiToLocal = (apiAddress: ApiAddress): Address => {
  console.log('🔄 Converting API address:', apiAddress);
  
  // Smart label handling: use custom label or generate from type
  const label = apiAddress.label || 'Address';
  
  // Use the addressType directly from API response
  const addressType = apiAddress.addressType;

  const converted = {
    id: apiAddress.id || `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    label: label,
    name: label, // Backward compatibility
    type: addressType, // Backward compatibility
    addressType: addressType, // New API field
    line1: apiAddress.line1,
    addressLine1: apiAddress.line1, // Backward compatibility
    line2: apiAddress.line2,
    addressLine2: apiAddress.line2, // Backward compatibility
    
    // Master data references - using state/city field names (IDs)
    state: apiAddress.state,
    city: apiAddress.city,
    
    // Extract display names from entities if available
    stateName: apiAddress.stateEntity?.stateName,
    cityName: apiAddress.cityEntity?.cityName,
    stateCode: apiAddress.stateEntity?.stateCode,
    
    postalCode: apiAddress.postalCode,
    pincode: apiAddress.postalCode, // Backward compatibility
    landmark: apiAddress.landmark, // Landmark field
    latitude: apiAddress.latitude,
    longitude: apiAddress.longitude,
    isDefault: apiAddress.default || false, // Backward compatibility
    default: apiAddress.default || false, // New API field
    isActive: apiAddress.isActive !== undefined ? apiAddress.isActive : true, // Default to active if not specified
    
    // Contact information for bookings
    contactName: apiAddress.contactName,
    contactPhone: apiAddress.contactPhone,
    
    createdAt: apiAddress.createdAt || new Date().toISOString(),
    updatedAt: apiAddress.updatedAt || new Date().toISOString(),
    addressUuid: apiAddress.addressUuid || '',
  };
  
  console.log('🔄 Converted to local format:', converted);
  return converted;
};

// Helper function to convert local address to API format
const convertLocalToApi = (localAddress: Partial<Address>): CreateAddressRequest => ({
  label: localAddress.label || localAddress.name || 'Address',
  addressType: localAddress.addressType || localAddress.type, // Map both old and new field names
  line1: localAddress.line1 || localAddress.addressLine1 || '',
  line2: localAddress.line2 || localAddress.addressLine2,
  
  // Store ONLY master data IDs - using state/city field names for minimal API changes
  state: localAddress.state || 0,
  city: localAddress.city || 0,
  
  postalCode: localAddress.postalCode || localAddress.pincode || '',
  landmark: localAddress.landmark, // Include landmark field
  latitude: localAddress.latitude,
  longitude: localAddress.longitude,
  default: localAddress.default !== undefined ? localAddress.default : localAddress.isDefault,
  isActive: localAddress.isActive !== undefined ? localAddress.isActive : true, // Default to active
  
  // Include contact information
  contactName: localAddress.contactName,
  contactPhone: localAddress.contactPhone,
});

// Check if user is authenticated and can use API
const canUseApi = (): boolean => {
  try {
    return authService.isAuthenticated();
  } catch {
    return false;
  }
};

// Fallback functions for localStorage
const getAddressesFromStorage = (): Address[] => {
  try {
    return safeJsonParse(localStorage.getItem('userAddresses'), []);
  } catch {
    return [];
  }
};

const saveAddressesToStorage = (addresses: Address[]): void => {
  try {
    localStorage.setItem('userAddresses', JSON.stringify(addresses));
    // Temporarily disabled to prevent event loops
    // window.dispatchEvent(new Event('addressesUpdated'));
  } catch (error) {
    console.error('Failed to save addresses to localStorage:', error);
  }
};

// Address Management Functions with API integration
export const getAddresses = async (): Promise<Address[]> => {
  console.log('📞 getAddresses called');
  
  try {
    if (canUseApi()) {
      console.log('🌐 Fetching from API');
      const apiAddresses = await addressService.getCurrentUserAddresses();
      console.log('✅ Raw API response:', apiAddresses);
      console.log('✅ API response type:', typeof apiAddresses, Array.isArray(apiAddresses));
      
      const localAddresses = apiAddresses.map(convertApiToLocal);
      console.log('🔄 Converted addresses:', localAddresses);
      
      saveAddressesToStorage(localAddresses);
      
      return localAddresses;
    } else {
      console.log('💾 Using localStorage');
      return getAddressesFromStorage();
    }
  } catch (error) {
    console.error('❌ getAddresses error:', error);
    return getAddressesFromStorage();
  }
};

// Synchronous version for immediate access (uses cached data)
export const getAddressesSync = (): Address[] => {
  return getAddressesFromStorage();
};

export const addAddress = async (address: Omit<Address, 'id' | 'createdAt' | 'updatedAt'>): Promise<Address> => {
  try {
    if (canUseApi()) {
      console.log('🌐 Creating address via API...');
      const apiRequest = convertLocalToApi(address);
      const apiAddress = await addressService.createAddress(apiRequest);
      const localAddress = convertApiToLocal(apiAddress);
      
      // Update localStorage cache
      const addresses = getAddressesFromStorage();
      
      // If this is the first address or marked as default, make it default
      const isFirstAddress = addresses.length === 0;
      const shouldBeDefault = address.isDefault || isFirstAddress;
      
      if (shouldBeDefault) {
        addresses.forEach(addr => addr.isDefault = false);
        localAddress.isDefault = true;
      }
      
      addresses.push(localAddress);
      saveAddressesToStorage(addresses);
      
      return localAddress;
    } else {
      console.log('💾 Creating address in localStorage (user not authenticated)');
      return addAddressToStorage(address);
    }
  } catch (error) {
    console.error('❌ Error creating address via API, falling back to localStorage:', error);
    return addAddressToStorage(address);
  }
};

// Fallback function for localStorage
const addAddressToStorage = (address: Omit<Address, 'id' | 'createdAt' | 'updatedAt'>): Address => {
  const addresses = getAddressesFromStorage();
  
  // If this is the first address or marked as default, make it default
  const isFirstAddress = addresses.length === 0;
  const shouldBeDefault = address.isDefault || isFirstAddress;
  
  // If making this default, remove default from others
  if (shouldBeDefault) {
    addresses.forEach(addr => addr.isDefault = false);
  }
  
  const newAddress: Address = {
    ...address,
    id: `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    label: address.label || address.name || 'Address',
    line1: address.line1 || address.addressLine1 || '',
    postalCode: address.postalCode || address.pincode || '',
    isDefault: shouldBeDefault,
    isActive: true, // New addresses are active by default
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  addresses.push(newAddress);
  saveAddressesToStorage(addresses);
  
  return newAddress;
};

export const updateAddress = async (addressId: string, updates: Partial<Address>): Promise<Address | null> => {
  try {
    if (canUseApi() && !addressId.startsWith('addr_')) {
      console.log('🌐 Updating address via API...');
      const apiRequest = convertLocalToApi(updates);
      const apiAddress = await addressService.updateAddress(addressId, apiRequest);
      const localAddress = convertApiToLocal(apiAddress);
      
      // Update localStorage cache
      const addresses = getAddressesFromStorage();
      const addressIndex = addresses.findIndex(addr => addr.id === addressId);
      
      if (addressIndex !== -1) {
        if (updates.isDefault) {
          addresses.forEach(addr => addr.isDefault = false);
          localAddress.isDefault = true;
        }
        
        addresses[addressIndex] = localAddress;
        saveAddressesToStorage(addresses);
      }
      
      return localAddress;
    } else {
      console.log('💾 Updating address in localStorage');
      return updateAddressInStorage(addressId, updates);
    }
  } catch (error) {
    console.error('❌ Error updating address via API, falling back to localStorage:', error);
    return updateAddressInStorage(addressId, updates);
  }
};

// Fallback function for localStorage
const updateAddressInStorage = (addressId: string, updates: Partial<Address>): Address | null => {
  const addresses = getAddressesFromStorage();
  const addressIndex = addresses.findIndex(addr => addr.id === addressId);
  
  if (addressIndex === -1) return null;
  
  // If making this default, remove default from others
  if (updates.isDefault) {
    addresses.forEach(addr => addr.isDefault = false);
  }
  
  const updatedAddress = {
    ...addresses[addressIndex],
    ...updates,
    updatedAt: new Date().toISOString()
  };
  
  addresses[addressIndex] = updatedAddress;
  saveAddressesToStorage(addresses);
  
  return updatedAddress;
};

export const deleteAddress = async (addressId: string): Promise<boolean> => {
  try {
    if (canUseApi() && !addressId.startsWith('addr_')) {
      console.log('🌐 Deleting address via API...');
      await addressService.deleteAddress(addressId);
      
      // Remove from localStorage cache
      const addresses = getAddressesFromStorage();
      const filteredAddresses = addresses.filter(addr => addr.id !== addressId);
      saveAddressesToStorage(filteredAddresses);
      
      return true;
    } else {
      console.log('💾 Deleting address from localStorage');
      return deleteAddressFromStorage(addressId);
    }
  } catch (error) {
    console.error('❌ Error deleting address via API, falling back to localStorage:', error);
    return deleteAddressFromStorage(addressId);
  }
};

// New function for deactivating address (soft delete)
export const deactivateAddress = async (addressId: string): Promise<boolean> => {
  try {
    if (canUseApi() && !addressId.startsWith('addr_')) {
      console.log('🌐 Deactivating address via API...');
      await addressService.deactivateAddress(addressId);
      
      // Remove from localStorage cache (since it won't be shown in UI)
      const addresses = getAddressesFromStorage();
      const filteredAddresses = addresses.filter(addr => addr.id !== addressId);
      saveAddressesToStorage(filteredAddresses);
      
      return true;
    } else {
      console.log('💾 Deactivating address in localStorage (marking as inactive)');
      // For localStorage, we'll just remove it from the UI by filtering it out
      return deleteAddressFromStorage(addressId);
    }
  } catch (error) {
    console.error('❌ Error deactivating address via API, falling back to localStorage:', error);
    return deleteAddressFromStorage(addressId);
  }
};

// Fallback function for localStorage
const deleteAddressFromStorage = (addressId: string): boolean => {
  const addresses = getAddressesFromStorage();
  const addressIndex = addresses.findIndex(addr => addr.id === addressId);
  
  if (addressIndex === -1) return false;
  
  const deletedAddress = addresses[addressIndex];
  addresses.splice(addressIndex, 1);
  
  // If deleted address was default, make the first remaining address default
  if (deletedAddress.isDefault && addresses.length > 0) {
    addresses[0].isDefault = true;
  }
  
  saveAddressesToStorage(addresses);
  
  return true;
};

export const getDefaultAddress = (): Address | null => {
  const addresses = getAddressesFromStorage();
  return addresses.find(addr => addr.isDefault) || null;
};

export const setDefaultAddress = async (addressId: string): Promise<boolean> => {
  try {
    if (canUseApi() && !addressId.startsWith('addr_')) {
      console.log('🌐 Setting default address via API...');
      await addressService.setDefaultAddress(addressId);
      
      // Update localStorage cache
      return setDefaultAddressInStorage(addressId);
    } else {
      console.log('💾 Setting default address in localStorage');
      return setDefaultAddressInStorage(addressId);
    }
  } catch (error) {
    console.error('❌ Error setting default address via API, falling back to localStorage:', error);
    return setDefaultAddressInStorage(addressId);
  }
};

// Fallback function for localStorage
const setDefaultAddressInStorage = (addressId: string): boolean => {
  const addresses = getAddressesFromStorage();
  
  // Remove default from all addresses
  addresses.forEach(addr => addr.isDefault = false);
  
  // Set new default
  const targetAddress = addresses.find(addr => addr.id === addressId);
  if (targetAddress) {
    targetAddress.isDefault = true;
    saveAddressesToStorage(addresses);
    return true;
  }
  
  return false;
};

// User Profile Management Functions (unchanged for now)
export const getUserProfile = (): UserProfile | null => {
  const profile = localStorage.getItem('userProfile');
  return profile ? JSON.parse(profile) : null;
};

export const updateUserProfile = (updates: Partial<UserProfile>): UserProfile => {
  const currentProfile = getUserProfile();
  
  const updatedProfile: UserProfile = {
    id: currentProfile?.id || `user_${Date.now()}`,
    name: '',
    email: '',
    phone: '',
    createdAt: currentProfile?.createdAt || new Date().toISOString(),
    ...currentProfile,
    ...updates,
    updatedAt: new Date().toISOString()
  };
  
  localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
  window.dispatchEvent(new Event('profileUpdated'));
  
  return updatedProfile;
};

export const createDefaultProfile = (): UserProfile => {
  const defaultProfile: UserProfile = {
    id: `user_${Date.now()}`,
    name: 'User',
    email: '',
    phone: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  localStorage.setItem('userProfile', JSON.stringify(defaultProfile));
  return defaultProfile;
};

// Utility Functions
export const getAddressCount = (): number => {
  return getAddressesFromStorage().length;
};

export const formatAddress = (address: Address): string => {
  const parts = [
    address.line1 || address.addressLine1,
    address.line2 || address.addressLine2,
    address.landmark,
    address.cityName || `City ID: ${address.city}`,  // Use resolved name or fallback to ID
    address.stateName || `State ID: ${address.state}`, // Use resolved name or fallback to ID
    address.postalCode || address.pincode
  ].filter(Boolean);
  
  return parts.join(', ');
};

export const validatePincode = (pincode: string): boolean => {
  return /^\d{6}$/.test(pincode);
};

export const validatePostalCode = (postalCode: string): boolean => {
  return /^\d{6}$/.test(postalCode);
};

export const getAddressByType = (type: Address['type']): Address[] => {
  return getAddressesFromStorage().filter(addr => addr.type === type);
};

// Sync addresses from API to localStorage (useful for app startup)
export const syncAddresses = async (): Promise<void> => {
  try {
    if (canUseApi()) {
      console.log('🔄 Syncing addresses from API to localStorage...');
      await getAddresses(); // This will fetch from API and cache in localStorage
      console.log('✅ Addresses synced successfully');
    }
  } catch (error) {
    console.error('❌ Error syncing addresses:', error);
  }
};

// ===== MASTER DATA INTEGRATION =====

/**
 * Get all states using master data service
 */
export const getStates = async (): Promise<State[]> => {
  try {
    return await masterDataService.getStates();
  } catch (error) {
    console.error('❌ Error fetching states:', error);
    return [];
  }
};

/**
 * Get all cities using master data service
 */
export const getCities = async (): Promise<City[]> => {
  try {
    return await masterDataService.getCities();
  } catch (error) {
    console.error('❌ Error fetching cities:', error);
    return [];
  }
};

/**
 * Get cities for a specific state
 */
export const getCitiesByState = async (stateId: number): Promise<City[]> => {
  try {
    return await masterDataService.getCitiesByState(stateId);
  } catch (error) {
    console.error(`❌ Error fetching cities for state ${stateId}:`, error);
    return [];
  }
};

/**
 * Get cities for a specific state by state code
 */
export const getCitiesByStateCode = async (stateCode: string): Promise<City[]> => {
  try {
    const states = await masterDataService.getStates();
    const state = states.find(s => s.stateCode.toLowerCase() === stateCode.toLowerCase());
    
    if (state) {
      return await masterDataService.getCitiesByState(state.stateId);
    }
    
    return [];
  } catch (error) {
    console.error(`❌ Error fetching cities for state code ${stateCode}:`, error);
    return [];
  }
};

/**
 * Get state by ID
 */
export const getStateById = async (stateId: number): Promise<State | null> => {
  try {
    return await masterDataService.getStateById(stateId);
  } catch (error) {
    console.error(`❌ Error fetching state ${stateId}:`, error);
    return null;
  }
};

/**
 * Get state by code
 */
export const getStateByCode = async (stateCode: string): Promise<State | null> => {
  try {
    const states = await masterDataService.getStates();
    return states.find(s => s.stateCode.toLowerCase() === stateCode.toLowerCase()) || null;
  } catch (error) {
    console.error(`❌ Error fetching state by code ${stateCode}:`, error);
    return null;
  }
};

/**
 * Get city by ID
 */
export const getCityById = async (cityId: number): Promise<City | null> => {
  try {
    return await masterDataService.getCityById(cityId);
  } catch (error) {
    console.error(`❌ Error fetching city ${cityId}:`, error);
    return null;
  }
};

/**
 * Search states by name
 */
export const searchStates = async (searchTerm: string): Promise<State[]> => {
  try {
    return await masterDataService.searchStates(searchTerm);
  } catch (error) {
    console.error('❌ Error searching states:', error);
    return [];
  }
};

/**
 * Search cities by name (optionally within a state)
 */
export const searchCities = async (searchTerm: string, stateId?: number): Promise<City[]> => {
  try {
    return await masterDataService.searchCities(searchTerm, stateId);
  } catch (error) {
    console.error('❌ Error searching cities:', error);
    return [];
  }
};

/**
 * Validate address against master data
 */
export const validateAddressAgainstMasterData = async (
  stateName: string,
  cityName: string
): Promise<{
  isValid: boolean;
  validatedState?: State;
  validatedCity?: City;
  suggestions?: {
    states: State[];
    cities: City[];
  };
}> => {
  try {
    // Search for matching state
    const stateMatches = await searchStates(stateName);
    const exactStateMatch = stateMatches.find(
      s => s.stateName.toLowerCase() === stateName.toLowerCase()
    );

    if (!exactStateMatch) {
      return {
        isValid: false,
        suggestions: {
          states: stateMatches.slice(0, 5), // Top 5 suggestions
          cities: [],
        },
      };
    }

    // Search for matching city within the state
    const cityMatches = await searchCities(cityName, exactStateMatch.stateId);
    const exactCityMatch = cityMatches.find(
      c => c.cityName.toLowerCase() === cityName.toLowerCase()
    );

    if (!exactCityMatch) {
      return {
        isValid: false,
        validatedState: exactStateMatch,
        suggestions: {
          states: [],
          cities: cityMatches.slice(0, 5), // Top 5 city suggestions
        },
      };
    }

    return {
      isValid: true,
      validatedState: exactStateMatch,
      validatedCity: exactCityMatch,
    };
  } catch (error) {
    console.error('❌ Error validating address:', error);
    return {
      isValid: false,
      suggestions: {
        states: [],
        cities: [],
      },
    };
  }
};

/**
 * Get formatted state and city options for dropdowns
 */
export const getFormattedStateOptions = async (): Promise<Array<{
  value: number;
  label: string;
  code: string;
}>> => {
  try {
    const states = await masterDataService.getStates();
    return states
      .sort((a, b) => a.stateName.localeCompare(b.stateName))
      .map(state => ({
        value: state.stateId,
        label: state.stateName,
        code: state.stateCode,
      }));
  } catch (error) {
    console.error('❌ Error getting formatted state options:', error);
    return [];
  }
};

/**
 * Get formatted city options for a specific state
 */
export const getFormattedCityOptions = async (stateId: number): Promise<Array<{
  value: number;
  label: string;
  stateId: number;
}>> => {
  try {
    const cities = await masterDataService.getCitiesByState(stateId);
    return cities
      .sort((a, b) => a.cityName.localeCompare(b.cityName))
      .map(city => ({
        value: city.cityId,
        label: city.cityName,
        stateId: city.stateId,
      }));
  } catch (error) {
    console.error(`❌ Error getting formatted city options for state ${stateId}:`, error);
    return [];
  }
};

/**
 * Refresh master data cache
 */
export const refreshMasterData = async (): Promise<void> => {
  try {
    console.log('🔄 Refreshing master data cache...');
    await masterDataService.refreshMasterData();
    console.log('✅ Master data cache refreshed');
  } catch (error) {
    console.error('❌ Error refreshing master data:', error);
  }
};